import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import type { CutTranscriptMode } from "../../../engine/clips/script/script.interfaces";

export type EditForClarityIntensity = "low" | "medium" | "heavy";

export type EditForClarityCategory =
    | "retake"
    | "filler_disfluency"
    | "redundant_repetition"
    | "off_topic_tangent"
    | "weak_hedging";

export type EditForClarityDetection = {
    id: string;
    categoryId: EditForClarityCategory;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    confidence: number;
    matchedText: string;
    reason: string;
};

export type DetectEditForClarityResult = {
    detections: EditForClarityDetection[];
    stats: {
        totalWords: number;
        totalCandidates: number;
        usedLlm: boolean;
    };
};

type PostAiEditForClarityFn = (args: {
    projectId: string;
    intensity: EditForClarityIntensity;
    maxDetections?: number;
    transcript: {
        text: string;
        tokens: Array<{
            tokenId: string;
            text: string;
            startSec: number;
            endSec: number;
            sourceIndex: number;
            speakerId?: string;
            speakerName?: string;
        }>;
    };
}) => Promise<
    | {
          detections?: Array<{
              id?: string;
              categoryId?: string;
              startTokenId?: string;
              endTokenId?: string;
              startSec?: number;
              endSec?: number;
              sourceIndex?: number;
              matchedText?: string;
              confidence?: number;
              reason?: string;
          }>;
      }
    | undefined
>;

type DetectEditForClarityArgs = {
    projectId: string;
    intensity: EditForClarityIntensity;
    postAiEditForClarity?: PostAiEditForClarityFn;
    maxDetections?: number;
    minConfidence?: number;
};

type ApplyEditForClarityArgs = {
    detections: EditForClarityDetection[];
    transcriptMode?: CutTranscriptMode;
};

type ClarityWord = {
    text: string;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    speakerId?: string;
    speakerName?: string;
};

const EPS = 1e-3;
const DEFAULT_MAX_DETECTIONS = 1200;
const DEFAULT_MIN_CONFIDENCE = 0;

const clamp = (value: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, value));

const toFinite = (value: any, fallback: number): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const normalizeCategory = (raw: any): EditForClarityCategory | null => {
    const value = String(raw ?? "")
        .trim()
        .toLowerCase()
        .replace(/-/g, "_");

    if (value === "retake" || value === "retakes") return "retake";
    if (value === "filler" || value === "filler_word" || value === "filler_words" || value === "filler_disfluency")
        return "filler_disfluency";
    if (value === "redundant" || value === "repetition" || value === "redundant_repetition")
        return "redundant_repetition";
    if (value === "offtopic" || value === "off_topic" || value === "off_topic_tangent" || value === "tangent")
        return "off_topic_tangent";
    if (value === "hedge" || value === "hedging" || value === "weak_hedging") return "weak_hedging";
    return null;
};

const joinWordsAsText = (words: Array<{ text: string }>): string => {
    let out = "";
    const noSpaceBefore = (t: string): boolean => /^[.,!?;:)\]]$/.test(t);
    for (const w of words) {
        const t = String(w?.text ?? "");
        if (!t) continue;
        if (out && !noSpaceBefore(t)) out += " ";
        out += t;
    }
    return out.trim();
};

const buildTranscriptText = (words: ClarityWord[]): string => {
    if (!Array.isArray(words) || words.length === 0) return "";
    let out = "";
    let prevSource = -1;
    let prevSpeaker = "";
    const noSpaceBefore = (t: string): boolean => /^[.,!?;:)\]]$/.test(t);

    for (const w of words) {
        const text = String(w?.text ?? "").trim();
        if (!text) continue;

        const sourceIndex = Number.isFinite(Number(w.sourceIndex)) ? Number(w.sourceIndex) : 0;
        const speaker = String(w.speakerName ?? "").trim() || String(w.speakerId ?? "").trim();

        if (out) {
            if (sourceIndex !== prevSource || speaker !== prevSpeaker) out += "\n";
            else if (!noSpaceBefore(text)) out += " ";
        }
        out += text;
        prevSource = sourceIndex;
        prevSpeaker = speaker;
    }

    return out.trim();
};

const buildTokenId = (word: ClarityWord, idx: number): string => {
    const sourceIndex = Number.isFinite(word?.sourceIndex) ? Number(word.sourceIndex) : 0;
    const start = Number.isFinite(word?.startSec) ? Number(word.startSec) : 0;
    const end = Number.isFinite(word?.endSec) ? Number(word.endSec) : start;
    return `efc_${sourceIndex}_${start.toFixed(6)}_${end.toFixed(6)}_${idx}`;
};

const getEditedTranscriptWordsForClarity = (): ClarityWord[] => {
    const inputWords = Array.isArray(ScriptLayerStore.transcriptWords)
        ? (ScriptLayerStore.transcriptWords as any[])
        : [];

    const out: ClarityWord[] = [];
    for (const w of inputWords) {
        const text = String((w as any)?.text ?? "").trim();
        if (!text) continue;
        const startSec = toFinite((w as any)?.startSec, NaN);
        const endSec = toFinite((w as any)?.endSec, startSec);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec < startSec) continue;
        const sourceIndex = Number((w as any)?.sourceIndex);
        const source = Number.isFinite(sourceIndex) ? sourceIndex : 0;

        // Ignore words that are already removed from playback.
        if (ScriptLayerStore.isTimeInDeleteCut(startSec)) continue;
        if (ScriptLayerStore.isTimeInGap(startSec)) continue;
        if (ScriptLayerStore.isTimeInStrikethroughCut(startSec)) continue;
        if (ScriptLayerStore.isTimeInRetakeCut(startSec)) continue;
        if (ScriptLayerStore.isTimeInTranscriptHiddenRange(startSec)) continue;

        out.push({
            text,
            startSec,
            endSec,
            sourceIndex: source,
            speakerId:
                (w as any)?.speakerId != null && String((w as any).speakerId).trim()
                    ? String((w as any).speakerId).trim()
                    : undefined,
            speakerName:
                typeof (w as any)?.speaker === "string" && String((w as any).speaker).trim()
                    ? String((w as any).speaker).trim()
                    : undefined,
        });
    }

    out.sort((a, b) => {
        if (a.startSec !== b.startSec) return a.startSec - b.startSec;
        if (a.endSec !== b.endSec) return a.endSec - b.endSec;
        return a.sourceIndex - b.sourceIndex;
    });
    return out;
};

const dedupeDetections = (detections: EditForClarityDetection[]): EditForClarityDetection[] => {
    if (!detections.length) return [];

    const byKey = new Map<string, EditForClarityDetection>();
    for (const d of detections) {
        const key = `${d.categoryId}|${Number(d.startSec).toFixed(3)}|${Number(d.endSec).toFixed(3)}|${d.sourceIndex}`;
        const prev = byKey.get(key);
        if (!prev || d.confidence > prev.confidence) byKey.set(key, d);
    }

    return [...byKey.values()].sort((a, b) => {
        if (a.startSec !== b.startSec) return a.startSec - b.startSec;
        if (a.endSec !== b.endSec) return a.endSec - b.endSec;
        return a.id.localeCompare(b.id);
    });
};

export async function detectEditForClarity(args: DetectEditForClarityArgs): Promise<DetectEditForClarityResult> {
    const words = getEditedTranscriptWordsForClarity();
    if (!words.length) {
        return {
            detections: [],
            stats: { totalWords: 0, totalCandidates: 0, usedLlm: false },
        };
    }
    if (!args.postAiEditForClarity) {
        return {
            detections: [],
            stats: { totalWords: words.length, totalCandidates: 0, usedLlm: false },
        };
    }

    const intensity: EditForClarityIntensity =
        args.intensity === "low" || args.intensity === "medium" || args.intensity === "heavy"
            ? args.intensity
            : "medium";
    const minConfidence = clamp(toFinite(args.minConfidence, DEFAULT_MIN_CONFIDENCE), 0, 0.99);
    const maxDetections = Math.max(1, Math.floor(toFinite(args.maxDetections, DEFAULT_MAX_DETECTIONS)));

    const tokens = words.map((w, idx) => ({
        tokenId: buildTokenId(w, idx),
        text: String(w.text ?? ""),
        startSec: Number(w.startSec ?? 0),
        endSec: Number(w.endSec ?? w.startSec ?? 0),
        sourceIndex: Number(w.sourceIndex ?? 0),
        speakerId: w.speakerId,
        speakerName: w.speakerName,
    }));
    const tokenIndexById = new Map(tokens.map((t, idx) => [t.tokenId, idx]));
    const transcriptText = buildTranscriptText(words);

    const raw = await args.postAiEditForClarity({
        projectId: String(args.projectId ?? ""),
        intensity,
        maxDetections,
        transcript: {
            text: transcriptText,
            tokens,
        },
    });

    const rows: any[] = Array.isArray(raw?.detections) ? raw!.detections : [];
    const out: EditForClarityDetection[] = [];

    for (const row of rows) {
        const categoryId = normalizeCategory((row as any)?.categoryId);
        if (!categoryId) continue;

        const startTokenId = String((row as any)?.startTokenId ?? "").trim();
        const endTokenId = String((row as any)?.endTokenId ?? "").trim();
        if (!startTokenId || !endTokenId) continue;

        const startIdxRaw = tokenIndexById.get(startTokenId);
        const endIdxRaw = tokenIndexById.get(endTokenId);
        if (startIdxRaw == null || endIdxRaw == null) continue;

        const startIdx = Math.max(0, Math.min(startIdxRaw, endIdxRaw));
        const endIdx = Math.min(words.length - 1, Math.max(startIdxRaw, endIdxRaw));
        if (endIdx < startIdx) continue;

        const rangeWords = words.slice(startIdx, endIdx + 1);
        if (!rangeWords.length) continue;

        const sourceFromRow = Number((row as any)?.sourceIndex);
        const sourceIndex = Number.isFinite(sourceFromRow)
            ? sourceFromRow
            : Number(rangeWords[0].sourceIndex ?? 0);

        const sourceWords = rangeWords.filter((w) => Number(w.sourceIndex ?? 0) === sourceIndex);
        const selectedWords = sourceWords.length > 0 ? sourceWords : rangeWords;
        const firstWord = selectedWords[0];
        const lastWord = selectedWords[selectedWords.length - 1];
        if (!firstWord || !lastWord) continue;

        const startSec = Number(firstWord.startSec ?? NaN);
        const endSec = Number(lastWord.endSec ?? firstWord.endSec ?? firstWord.startSec ?? NaN);
        if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + EPS) continue;

        const confidence = clamp(toFinite((row as any)?.confidence, 0), 0, 1);
        if (confidence < minConfidence) continue;

        const matchedText = String((row as any)?.matchedText ?? "").trim() || joinWordsAsText(selectedWords);
        const reason = String((row as any)?.reason ?? "").trim();
        const idRaw = String((row as any)?.id ?? "").trim();
        const id = idRaw || `clarity_${categoryId}_${startTokenId}_${endTokenId}`;

        out.push({
            id,
            categoryId,
            startSec,
            endSec,
            sourceIndex,
            confidence,
            matchedText,
            reason,
        });
    }

    return {
        detections: dedupeDetections(out),
        stats: {
            totalWords: words.length,
            totalCandidates: rows.length,
            usedLlm: raw != null,
        },
    };
}

export async function applyEditForClarityDetections(args: ApplyEditForClarityArgs): Promise<{ appliedCount: number }> {
    const detections = dedupeDetections(Array.isArray(args.detections) ? args.detections : []);
    if (!detections.length) return { appliedCount: 0 };

    const modeFromArgs: CutTranscriptMode | undefined = args.transcriptMode;
    const ordered = [...detections].sort((a, b) => b.startSec - a.startSec);
    let appliedCount = 0;

    CompositionService.beginScriptLayerBatch();
    try {
        for (const d of ordered) {
            const startSec = Number(d.startSec ?? NaN);
            const endSec = Number(d.endSec ?? NaN);
            if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + EPS) continue;
            const mode: CutTranscriptMode =
                modeFromArgs ?? (d.categoryId === "retake" ? "retake-strikethrough" : "strikethrough");
            try {
                await CompositionService.cutScriptLayerRangeWithAnalysis(startSec, endSec, d.sourceIndex, {
                    transcriptMode: mode,
                });
            } catch {
                CompositionService.cutScriptLayerRange(startSec, endSec, { transcriptMode: mode });
            }
            appliedCount += 1;
        }
    } finally {
        await CompositionService.endScriptLayerBatch();
    }

    return { appliedCount };
}
